'use client';

import { useState, useEffect, useCallback, useMemo, createContext, useContext, ReactNode } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { API_BASE, API_ENDPOINTS, PROTECTED_ROUTES, PUBLIC_ROUTES } from '@/config/api.config';

interface User {
  id: string;
  email: string;
  name: string | null;
  isAdmin: boolean;
  emailVerified: boolean;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  getToken: () => string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Get stored auth token
 */
function getStoredToken(): string | null {
  if (typeof globalThis.window === 'undefined') return null;
  return localStorage.getItem('token');
}

/**
 * Store auth token
 */
function setStoredToken(token: string): void {
  localStorage.setItem('token', token);
}

/**
 * Remove auth token
 */
function removeStoredToken(): void {
  localStorage.removeItem('token');
}

/**
 * Check if a path requires authentication
 */
function isProtectedRoute(pathname: string): boolean {
  return PROTECTED_ROUTES.some(route => pathname.startsWith(route));
}

/**
 * Check if a path is public
 */
function isPublicRoute(pathname: string): boolean {
  // Exact match for root
  if (pathname === '/') return true;
  
  // Check if path starts with any public route
  return PUBLIC_ROUTES.some(route => {
    if (route === '/') return false; // Already handled above
    return pathname.startsWith(route);
  });
}

/**
 * Auth Provider Component
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const router = useRouter();
  const pathname = usePathname();

  const fetchUser = useCallback(async () => {
    const token = getStoredToken();
    
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_BASE}${API_ENDPOINTS.auth.user}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        credentials: 'include',
      });

      if (!response.ok) {
        if (response.status === 401) {
          removeStoredToken();
          setUser(null);
        }
        throw new Error('Failed to fetch user');
      }

      const data = await response.json();
      setUser(data);
      setError(null);
    } catch (err) {
      console.error('Auth error:', err);
      setUser(null);
      setError('Failed to authenticate');
    } finally {
      setLoading(false);
    }
  }, []);

  // Check auth on mount and pathname change
  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  // Redirect logic for protected routes
  useEffect(() => {
    if (loading) return;

    const isProtected = isProtectedRoute(pathname);

    // Redirect to login if trying to access protected route without auth
    if (isProtected && !user) {
      const returnUrl = encodeURIComponent(pathname);
      router.push(`/login?returnUrl=${returnUrl}`);
    }

    // Redirect to dashboard if logged in and on login/register page
    if (user && (pathname === '/login' || pathname === '/register')) {
      router.push('/dashboard');
    }
  }, [loading, user, pathname, router]);

  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      setError(null);
      const response = await fetch(`${API_BASE}${API_ENDPOINTS.auth.login}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Login failed');
        return false;
      }

      if (data.token) {
        setStoredToken(data.token);
      }
      
      setUser(data.user);
      return true;
    } catch (err) {
      setError('Network error. Please try again.');
      return false;
    }
  };

  const logout = async () => {
    try {
      const token = getStoredToken();
      if (token) {
        await fetch(`${API_BASE}${API_ENDPOINTS.auth.logout}`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
          },
          credentials: 'include',
        });
      }
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      removeStoredToken();
      setUser(null);
      router.push('/login');
    }
  };

  const refreshUser = async () => {
    await fetchUser();
  };

  const getToken = () => getStoredToken();

  const value = useMemo<AuthContextType>(() => ({
    user,
    loading,
    error,
    isAuthenticated: !!user,
    login,
    logout,
    refreshUser,
    getToken,
  }), [user, loading, error]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Hook to use auth context
 */
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

/**
 * Hook for requiring authentication on a page
 * Returns loading state and user, redirects to login if not authenticated
 */
export function useRequireAuth() {
  const { user, loading, isAuthenticated } = useAuth();
  const router = useRouter();
  const pathname = usePathname();

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      const returnUrl = encodeURIComponent(pathname);
      router.push(`/login?returnUrl=${returnUrl}`);
    }
  }, [loading, isAuthenticated, router, pathname]);

  return { user, loading, isAuthenticated };
}

/**
 * Hook for requiring admin access
 */
export function useRequireAdmin() {
  const { user, loading, isAuthenticated } = useRequireAuth();
  const router = useRouter();

  useEffect(() => {
    if (!loading && isAuthenticated && !user?.isAdmin) {
      router.push('/dashboard');
    }
  }, [loading, isAuthenticated, user, router]);

  return { user, loading, isAdmin: user?.isAdmin ?? false };
}
